/**
 * Onboarding Telemetry Hook
 *
 * Tracks user interactions during onboarding for analytics.
 * MVP: Stores events in localStorage buffer.
 * Future: Sends to backend analytics endpoint.
 */

import { useCallback, useRef, useEffect } from 'react';
import type { TelemetryEvent, TelemetryEventType, ModuleId } from '../types/onboarding';
import { STORAGE_KEYS } from '../lib/onboarding/constants';
import { useOnboardingStore } from '../stores/onboardingStore';

// Buffer size before attempting to flush
const BUFFER_FLUSH_SIZE = 10;
const BUFFER_FLUSH_INTERVAL_MS = 30000; // 30 seconds

interface TelemetryHookReturn {
  trackEvent: (type: TelemetryEventType, metadata?: Record<string, unknown>) => void;
  trackModuleView: (moduleId: ModuleId) => void;
  trackModuleComplete: (moduleId: ModuleId, timeSpentSeconds: number) => void;
  trackQuizStart: (quizType: 'security' | 'system') => void;
  trackQuizComplete: (quizType: 'security' | 'system', score: number, passed: boolean) => void;
  trackDropOff: (moduleId: ModuleId, reason?: string) => void;
  flushEvents: () => Promise<void>;
}

export function useOnboardingTelemetry(): TelemetryHookReturn {
  const { role, onboardingVersion } = useOnboardingStore();
  const bufferRef = useRef<TelemetryEvent[]>([]);
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load any existing buffered events from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.telemetryBuffer);
      if (stored) {
        bufferRef.current = JSON.parse(stored);
      }
    } catch {
      // Ignore parse errors
    }

    // Set up periodic flush
    flushTimerRef.current = setInterval(() => {
      if (bufferRef.current.length > 0) {
        flushEvents();
      }
    }, BUFFER_FLUSH_INTERVAL_MS);

    return () => {
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
      }
      // Save any remaining events on unmount
      saveBuffer();
    };
  }, []);

  // Save buffer to localStorage
  const saveBuffer = useCallback(() => {
    try {
      localStorage.setItem(
        STORAGE_KEYS.telemetryBuffer,
        JSON.stringify(bufferRef.current)
      );
    } catch {
      // localStorage full or disabled
    }
  }, []);

  // Flush events to backend (MVP: just clear buffer)
  const flushEvents = useCallback(async () => {
    if (bufferRef.current.length === 0) return;

    // MVP: Log to console and clear buffer
    // Future: Send to /api/telemetry endpoint
    if (import.meta.env.DEV) {
      console.log('[Telemetry] Flushing events:', bufferRef.current);
    }

    // Clear buffer
    bufferRef.current = [];
    localStorage.removeItem(STORAGE_KEYS.telemetryBuffer);
  }, []);

  // Add event to buffer
  const addEvent = useCallback(
    (event: TelemetryEvent) => {
      bufferRef.current.push(event);
      saveBuffer();

      // Flush if buffer is full
      if (bufferRef.current.length >= BUFFER_FLUSH_SIZE) {
        flushEvents();
      }
    },
    [saveBuffer, flushEvents]
  );

  // Generic event tracker
  const trackEvent = useCallback(
    (type: TelemetryEventType, metadata?: Record<string, unknown>) => {
      const event: TelemetryEvent = {
        type,
        timestamp: new Date().toISOString(),
        metadata: {
          ...metadata,
          role,
          onboardingVersion,
        },
      };
      addEvent(event);
    },
    [role, onboardingVersion, addEvent]
  );

  // Module view tracker
  const trackModuleView = useCallback(
    (moduleId: ModuleId) => {
      trackEvent('module_viewed', { moduleId });
    },
    [trackEvent]
  );

  // Module completion tracker
  const trackModuleComplete = useCallback(
    (moduleId: ModuleId, timeSpentSeconds: number) => {
      trackEvent('module_completed', { moduleId, timeSpentSeconds });
    },
    [trackEvent]
  );

  // Quiz start tracker
  const trackQuizStart = useCallback(
    (quizType: 'security' | 'system') => {
      trackEvent('quiz_started', { quizType });
    },
    [trackEvent]
  );

  // Quiz completion tracker
  const trackQuizComplete = useCallback(
    (quizType: 'security' | 'system', score: number, passed: boolean) => {
      const eventType = passed ? 'quiz_completed' : 'quiz_failed';
      trackEvent(eventType, { quizType, score, passed });
    },
    [trackEvent]
  );

  // Drop-off tracker (user abandons onboarding)
  const trackDropOff = useCallback(
    (moduleId: ModuleId, reason?: string) => {
      trackEvent('drop_off', { moduleId, reason });
      // Flush immediately on drop-off
      flushEvents();
    },
    [trackEvent, flushEvents]
  );

  return {
    trackEvent,
    trackModuleView,
    trackModuleComplete,
    trackQuizStart,
    trackQuizComplete,
    trackDropOff,
    flushEvents,
  };
}

// ============================================================================
// Utility hook for tracking time spent on a module
// ============================================================================

export function useModuleTimer(moduleId: ModuleId) {
  const startTimeRef = useRef<number>(Date.now());
  const { trackModuleComplete } = useOnboardingTelemetry();

  const getTimeSpent = useCallback(() => {
    return Math.round((Date.now() - startTimeRef.current) / 1000);
  }, []);

  const completeWithTime = useCallback(() => {
    const timeSpent = getTimeSpent();
    trackModuleComplete(moduleId, timeSpent);
  }, [moduleId, trackModuleComplete, getTimeSpent]);

  // Reset timer when module changes
  useEffect(() => {
    startTimeRef.current = Date.now();
  }, [moduleId]);

  return {
    getTimeSpent,
    completeWithTime,
  };
}
